import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVipSource } from '../../context/VipSourceContext';

export default function PlayerSearchPage() {
  const navigate = useNavigate();
  const source = useVipSource();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const search = async (q = query) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) return;
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const res = await source.api.searchPlayers(trimmed);
      setResults(res.data.results || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') search();
  };

  const openPlayer = (userId) => {
    navigate(`${source.paths.player}/${userId}?from=dashboard`);
  };

  return (
    <div className="max-w-[640px] mx-auto">
      <h1 className="text-lg font-bold text-ink mb-1">Player lookup</h1>
      <p className="text-xs text-ink-dim mb-6">
        Search by email address or player ID{source.isPrivate ? ' among your players' : ''}.
      </p>

      {/* Search input */}
      <div className="flex gap-2 mb-6">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Email or player ID…"
          autoFocus
          className="flex-1 rounded px-3 py-2 text-sm bg-[#1a1e28] border border-[#2a3042] text-[#d8dde8] placeholder-[#3d4558] outline-none focus:border-[#d4a843] transition-colors"
        />
        <button
          onClick={() => search()}
          disabled={loading || query.trim().length < 2}
          className="px-4 py-2 rounded text-sm font-bold transition-colors"
          style={{
            background: query.trim().length >= 2 ? '#d4a843' : '#1f2430',
            color: query.trim().length >= 2 ? '#13161b' : '#3d4558',
            cursor: query.trim().length >= 2 ? 'pointer' : 'default',
            border: 'none',
          }}
        >
          {loading ? '…' : 'Search'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-[#e05555] mb-4">{error}</p>
      )}

      {/* Results */}
      {results !== null && (
        results.length === 0 ? (
          <p className="text-xs text-ink-dim">No players found for <span className="text-[#d8dde8]">"{query}"</span>.</p>
        ) : (
          <div className="rounded border border-[#2a3042] overflow-hidden">
            <div className="px-4 py-2 border-b border-[#1f2430]">
              <span className="text-[10px] text-[#5a6477]">{results.length} result{results.length !== 1 ? 's' : ''}</span>
            </div>
            {results.map((r) => (
              <div
                key={r.user_id}
                onClick={() => openPlayer(r.user_id)}
                className="flex items-center justify-between px-4 py-3 border-b border-[#1f2430] last:border-0 cursor-pointer transition-colors"
                style={{ background: 'transparent' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1f2430'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <p className="text-sm text-[#d8dde8] font-medium">{r.email || `User ${r.user_id}`}</p>
                  <p className="text-[10px] text-[#5a6477] mt-0.5">ID {r.user_id}</p>
                </div>
                <span className="text-[10px] text-[#3d4558]">→</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
